import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Renders the item in a destructive (red) style. */
  destructive?: boolean;
  /** Optional leading icon (inherits the item's text colour via currentColor). */
  icon?: React.ReactNode;
}

/** A visual separator between groups of items. */
export interface ActionMenuDivider {
  divider: true;
}

export type ActionMenuEntry = ActionMenuItem | ActionMenuDivider;

const isDivider = (entry: ActionMenuEntry): entry is ActionMenuDivider =>
  'divider' in entry;

interface ActionMenuProps {
  items: ActionMenuEntry[];
  /** Accessible label for the trigger button. */
  ariaLabel?: string;
}

/**
 * Row action menu: a kebab (⋯) trigger that opens a small dropdown. The menu is
 * rendered in a portal and positioned with fixed coordinates so it is never
 * clipped by the table's scroll/overflow container.
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({ items, ariaLabel = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_WIDTH = 160;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Right-align the menu under the trigger, clamped to the viewport.
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setCoords({ top: rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const close = () => setOpen(false);

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          // Stop the row's own onClick (rows are often clickable) from firing.
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="inline-flex items-center justify-center rounded-md p-1.5 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
      >
        <MoreHorizontal size={18} color="#404040" aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: MENU_WIDTH,
              zIndex: 60,
            }}
            className="rounded-lg border border-neutral-200 bg-white py-1 shadow-lg"
          >
            {items.map((entry, index) =>
              isDivider(entry) ? (
                <hr
                  key={`divider-${index}`}
                  className="my-1 border-neutral-200"
                  aria-hidden="true"
                />
              ) : (
                <button
                  key={entry.label}
                  type="button"
                  role="menuitem"
                  disabled={entry.disabled}
                  onClick={(e) => {
                    // Portal clicks still bubble through the React tree to the row.
                    e.stopPropagation();
                    setOpen(false);
                    entry.onClick();
                  }}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    fontFamily: 'Oxygen',
                    color: entry.destructive ? '#B42318' : '#404040',
                  }}
                >
                  {entry.icon && (
                    <span className="flex-shrink-0" aria-hidden="true">
                      {entry.icon}
                    </span>
                  )}
                  {entry.label}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default ActionMenu;
